import React, { FC, useCallback } from 'react';
import clsx from 'clsx';

import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { SheetsImportInfo } from '@futrli-sdk/predict';
import { useLocalStorage } from '@futrli/shared-services';

import { DeleteButton } from '../../controls/delete-button/delete-button';
import { LOCAL_STORAGE_REDIRECT_KEY } from '../../constants';
import { trackEvent } from '../../../../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';

import styles from './introduction.scss';

interface Props {
  hasCreatedImportSheet: boolean;
  isSheetAccessible: boolean | undefined;
  sheetInfo: SheetsImportInfo | null;
  hasValidAuthToken: boolean;
  authURL: string | undefined;
}

const IntroductionTextContent = ({
  hasCreatedImportSheet,
  isSheetAccessible,
  sheetInfo,
  hasValidAuthToken,
  authURL
}: Props) => {
  const intl = useIntl();
  const [, setLoginRedirectUrl] = useLocalStorage(LOCAL_STORAGE_REDIRECT_KEY, '');

  const handleAuthorisationAction = useCallback(async () => {
    setLoginRedirectUrl(window.location.pathname);
    trackEvent(TRACKING_EVENTS.IMPORT_AUTHORISE);
    window.open(authURL, '_blank');
  }, [authURL, setLoginRedirectUrl]);

  if (!hasValidAuthToken) {
    return (
      <div className={styles.errorMessage} data-testid="import-sheet-auth-error">
        <Icon type="error" color="#f07272" />
        <FormattedMessage
          id="import.access_auth_error"
          defaultMessage="Your Google session has expired. Please click continue to re-authenticate with Google and access your Master sheet."
        />
        <Button
          onClick={handleAuthorisationAction}
          buttonType="primary"
          data-testid="import-auth-dialog-actions-primary"
        >
          <FormattedMessage id="common.continue" defaultMessage="Continue" />
        </Button>
      </div>
    );
  }

  if (hasCreatedImportSheet && !isSheetAccessible) {
    return (
      <div className={styles.errorMessage} data-testid="import-sheet-error">
        <Icon type="error" color="#f07272" />
        <FormattedMessage
          id="import.access_error"
          defaultMessage="Unable to access the master google sheet file. Please reload the page. If the problem persists, please ask <b> the workspace owner </b> to reset the link, create a new Master sheet and add their data."
          values={{
            b: (chunks: string) => <b>{chunks}</b>
          }}
        />
        <DeleteButton title={intl.formatMessage({ id: 'common.reset', defaultMessage: 'Reset' })} />
      </div>
    );
  }

  if (sheetInfo) {
    return (
      <p data-testid="import-sheet-info">
        <FormattedMessage
          id="import.introduction_with_sheet"
          defaultMessage="A master file was created by {firstName} {lastName} ({email}). They will need to give you access before you can access it (this is done by opening the file and using the share functionality)."
          values={{
            firstName: sheetInfo.owner_user.first_name,
            lastName: sheetInfo.owner_user.last_name,
            email: sheetInfo.owner_user.email
          }}
        />
      </p>
    );
  }

  return (
    <React.Fragment>
      <p data-testid="import-sheet-intro">
        <FormattedMessage
          id="import.introduction_without_sheet"
          defaultMessage="To use these Google Sheets templates, we need to create a Master Sheets file. This is the sheet that will be hot-linked to Futrli. Follow these steps and you’ll have your first template in a few clicks."
        />
      </p>
      <p>
        <FormattedMessage
          id="import.introduction_without_sheet_astrisk"
          defaultMessage="*Before editing, please make sure that templates are copied into your Master sheet: it has the hot-link!"
        />
      </p>
    </React.Fragment>
  );
};

export const Introduction: FC<Props> = props => {
  return (
    <div className="columns">
      <div className={clsx('column', 'is-12', styles.introduction)}>
        <IntroductionTextContent {...props} />
      </div>
    </div>
  );
};
